import React, { useId } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ChartType, DataPoint } from './types';
import { getChartColors, formatChartValue } from './dataUtils';

interface ChartDisplayProps {
  data: DataPoint[];
  chartType: ChartType;
  valueKeys: string[];
  labelKey: string;
  height?: number;
}

const COLORS = getChartColors();

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-4 py-3">
      <p className="text-sm font-medium text-gray-900 mb-1">{label}</p>
      {payload.map((entry: any, idx: number) => (
        <p key={idx} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: {Number(entry.value).toLocaleString('pt-BR')}
        </p>
      ))}
    </div>
  );
};

const PieTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null;
  const entry = payload[0];

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-4 py-3">
      <p className="text-sm font-medium text-gray-900">{entry.name}</p>
      <p className="text-sm text-gray-600">
        {Number(entry.value).toLocaleString('pt-BR')} ({((entry.percent || 0) * 100).toFixed(1)}%)
      </p>
    </div>
  );
};

const renderPieLabel = ({ name, percent }: any) => {
  if (percent < 0.05) return null;
  return `${name} (${(percent * 100).toFixed(0)}%)`;
};

const ChartDisplay: React.FC<ChartDisplayProps> = ({ data, chartType, valueKeys, labelKey, height = 350 }) => {
  const gradientId = useId().replace(/:/g, '');

  if (!data || data.length === 0) return null;

  const compact = height < 320;
  const pieOuterRadius = Math.min(120, Math.max(72, height / 3));

  const renderBarChart = () => (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 10, right: compact ? 12 : 30, left: 0, bottom: compact ? 24 : 40 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 11, fill: '#6b7280' }}
          angle={compact ? -18 : -30}
          textAnchor="end"
          height={compact ? 44 : 60}
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#6b7280' }}
          tickFormatter={formatChartValue}
        />
        <Tooltip content={<CustomTooltip />} />
        {valueKeys.length > 1 && <Legend />}
        {valueKeys.map((key, idx) => (
          <Bar
            key={key}
            dataKey={key}
            fill={COLORS[idx % COLORS.length]}
            radius={[4, 4, 0, 0]}
            maxBarSize={50}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );

  const renderPieChart = () => (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={true}
          label={compact ? false : renderPieLabel}
          outerRadius={pieOuterRadius}
          dataKey="value"
          nameKey="name"
          animationBegin={0}
          animationDuration={800}
        >
          {data.map((_entry, idx) => (
            <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip content={<PieTooltip />} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );

  const renderLineChart = () => (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 10, right: compact ? 12 : 30, left: 0, bottom: compact ? 24 : 40 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 11, fill: '#6b7280' }}
          angle={compact ? -18 : -30}
          textAnchor="end"
          height={compact ? 44 : 60}
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#6b7280' }}
          tickFormatter={formatChartValue}
        />
        <Tooltip content={<CustomTooltip />} />
        {valueKeys.length > 1 && <Legend />}
        {valueKeys.map((key, idx) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={COLORS[idx % COLORS.length]}
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );

  const renderAreaChart = () => (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 10, right: compact ? 12 : 30, left: 0, bottom: compact ? 24 : 40 }}>
        <defs>
          {valueKeys.map((key, idx) => (
            <linearGradient key={key} id={`${gradientId}-gradient-${idx}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS[idx % COLORS.length]} stopOpacity={0.3} />
              <stop offset="95%" stopColor={COLORS[idx % COLORS.length]} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 11, fill: '#6b7280' }}
          angle={compact ? -18 : -30}
          textAnchor="end"
          height={compact ? 44 : 60}
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#6b7280' }}
          tickFormatter={formatChartValue}
        />
        <Tooltip content={<CustomTooltip />} />
        {valueKeys.length > 1 && <Legend />}
        {valueKeys.map((key, idx) => (
          <Area
            key={key}
            type="monotone"
            dataKey={key}
            stroke={COLORS[idx % COLORS.length]}
            fillOpacity={1}
            fill={`url(#${gradientId}-gradient-${idx})`}
            strokeWidth={2}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );

  const chartRenderers: Record<ChartType, () => JSX.Element> = {
    bar: renderBarChart,
    pie: renderPieChart,
    line: renderLineChart,
    area: renderAreaChart,
  };

  return (
    <div className="w-full bg-white rounded-lg p-3 sm:p-4">
      {chartRenderers[chartType]()}
    </div>
  );
};

export default ChartDisplay;
